import React from "react";
import { useTranslation } from "react-i18next";
import { Users, Mail } from "lucide-react";
import Seo from "@/components/Seo";
import ContactForm from "@/components/forms/ContactForm";
import { useLang } from "@/i18n/LangContext";

const SEO = {
  nl: { title: "Over ons — de vereniging achter Lemmer Leeft", description: "Lemmer Leeft is de nieuwe koers van BIZ Vereniging Lemmer, de ondernemersvereniging van het centrum. Lees wie we zijn en maak kennis met het bestuur." },
  de: { title: "Über uns — der Verein hinter Lemmer Leeft", description: "Lemmer Leeft ist die neue Ausrichtung des BIZ Vereniging Lemmer, des Unternehmervereins des Zentrums. Lernen Sie uns und den Vorstand kennen." },
  en: { title: "About us — the association behind Lemmer Leeft", description: "Lemmer Leeft is the new direction of BIZ Vereniging Lemmer, the town-centre business association. Meet the team behind it." },
};

const CONTENT = {
  nl: {
    eyebrow: "Wie we zijn",
    h1: "Over ons",
    sec1: { title: "Een frisse start voor het centrum", body: "Lemmer Leeft is de nieuwe naam en frisse koers van BIZ Vereniging Lemmer, de ondernemersvereniging van het centrum van Lemmer. Met een nieuwe naam, een nieuw logo, een moderne website en een vernieuwde aanpak zetten we een duidelijke stap. Ons doel is simpel: zorgen voor een bruisend Lemmer waar écht wat te beleven valt." },
    sec2: { title: "Waarom Lemmer Leeft?", body1: "De term \"BIZ\" (Bedrijveninvesteringszone) is nogal technisch en zegt de meeste mensen weinig. \"Lemmer Leeft\" straalt precies uit waar we voor gaan: een levendig, gastvrij dorp aan het water.", body2: "Alle ondernemers in het centrum dragen samen bij aan de vereniging. Daarmee werken we aan een aantrekkelijk centrum, met evenementen, sfeer, promotie en samenwerking. Zo blijft Lemmer een fijne plek om te winkelen, uit te gaan en te verblijven, voor inwoners én bezoekers." },
    sec3: { title: "Grote ambities", body: "Achter de schermen wordt hard doorgewerkt, met een vernieuwd bestuur en een grotere evenementencommissie. De plannen zijn ambitieus. Samen met alle ondernemers en inwoners willen we van Lemmer de meest levendige en gezellige plek van de regio maken." },
    bestuur: { eyebrow: "Team", title: "Het bestuur", body: "Achter Lemmer Leeft staat een enthousiast bestuur van BIZ Vereniging Lemmer." },
    contact: { title: "Vragen of ideeën?", body: "We horen graag van je via het contactformulier hieronder." },
  },
  de: {
    eyebrow: "Wer wir sind",
    h1: "Über uns",
    sec1: { title: "Ein frischer Start für das Zentrum", body: "Lemmer Leeft ist der neue Name und die neue Ausrichtung von BIZ Vereniging Lemmer, dem Unternehmerverein des Zentrums von Lemmer. Mit einem neuen Namen, einem neuen Logo, einer modernen Website und einem erneuerten Ansatz setzen wir ein klares Zeichen. Unser Ziel ist einfach: für ein lebendiges Lemmer sorgen, in dem wirklich etwas los ist." },
    sec2: { title: "Warum Lemmer Leeft?", body1: "Der Begriff \"BIZ\" (Unternehmensinvestitionszone) klingt technisch und sagt vielen wenig. \"Lemmer Leeft\" bringt genau das rüber, wofür wir stehen: ein lebendiges, gastfreundliches Dorf am Wasser.", body2: "Alle Unternehmer im Zentrum tragen gemeinsam zum Verein bei. So arbeiten wir an einem attraktiven Zentrum mit Veranstaltungen, Atmosphäre, Werbung und Zusammenarbeit — für Einwohner und Besucher." },
    sec3: { title: "Große Pläne", body: "Hinter den Kulissen wird kräftig weitergearbeitet, mit einem erneuerten Vorstand und einer größeren Veranstaltungskommission. Die Pläne sind ehrgeizig. Gemeinsam mit allen Unternehmern und Einwohnern wollen wir Lemmer zum lebendigsten und gemütlichsten Ort der Region machen." },
    bestuur: { eyebrow: "Team", title: "Der Vorstand", body: "Hinter Lemmer Leeft steht ein engagierter Vorstand von BIZ Vereniging Lemmer." },
    contact: { title: "Fragen oder Ideen?", body: "Wir freuen uns über Ihre Nachricht über das Kontaktformular unten." },
  },
  en: {
    eyebrow: "Who we are",
    h1: "About us",
    sec1: { title: "A fresh start for the town centre", body: "Lemmer Leeft is the new name and direction of BIZ Vereniging Lemmer, the business association of the Lemmer town centre. With a new name, a new logo, a modern website and a renewed approach we're taking a clear step forward. Our goal is simple: to make Lemmer a lively place with plenty to experience." },
    sec2: { title: "Why Lemmer Leeft?", body1: "The term \"BIZ\" (Business Investment Zone) sounds technical and means little to most people. \"Lemmer Leeft\" tells you exactly what we stand for: a lively, welcoming village on the water.", body2: "All town-centre entrepreneurs contribute to the association. Together we work on an attractive centre with events, atmosphere, promotion and cooperation — for residents and visitors alike." },
    sec3: { title: "Big ambitions", body: "Behind the scenes we're working hard, with a renewed board and a larger events committee. Our plans are ambitious. Together with all entrepreneurs and residents we aim to make Lemmer the most lively and enjoyable spot in the region." },
    bestuur: { eyebrow: "Team", title: "The board", body: "Behind Lemmer Leeft stands an enthusiastic board of BIZ Vereniging Lemmer." },
    contact: { title: "Questions or ideas?", body: "We'd love to hear from you via the contact form below." },
  },
};

const BESTUUR = [
  { name: "Herman Teuling", role: "Voorzitter, Penningmeester & Marketing" },
  { name: "Bob Vermeeren-de Groot", role: "Secretaris" },
  { name: "Petra van Schaik", role: "Algemeen bestuurslid" },
  { name: "Wilma Venema-Heij", role: "Algemeen bestuurslid" },
  { name: "Kevin Dam", role: "Algemeen bestuurslid (Evenementen)" },
  { name: "Conny Ruiter-Dölly", role: "Evenementencommissie" },
  { name: "Sylvia Teuling", role: "Evenementencommissie" },
  { name: "Rika Bovee", role: "Evenementencommissie" },
  { name: "Auke van der Heide", role: "Evenementencommissie" },
];

const ContentEyebrow = ({ children }) => (
  <span className="inline-block text-[#2E9CD6] font-[Poppins] font-semibold text-xs uppercase tracking-[0.2em] mb-3">
    {children}
  </span>
);

const AccentLine = () => (
  <div className="flex items-center gap-3 mb-6" aria-hidden="true">
    <span className="block w-10 h-[3px] rounded-full bg-[#2E9CD6]" />
    <svg width="60" height="10" viewBox="0 0 60 10" className="text-[#6BC0E8]">
      <path
        d="M0,5 C10,0 20,10 30,5 C40,0 50,10 60,5"
        stroke="currentColor"
        strokeWidth="2"
        fill="none"
        strokeLinecap="round"
      />
    </svg>
  </div>
);

export const OverOnsPage = () => {
  const { lang } = useLang();
  const c = CONTENT[lang] || CONTENT.nl;
  const seo = SEO[lang] || SEO.nl;
  useTranslation(); // ensure re-render on lang change

  return (
    <div data-testid="over-ons-page" className="bg-white">
      <Seo title={seo.title} description={seo.description} lang={lang} />

      {/* Header */}
      <section className="ll-section-soft py-14 sm:py-20">
        <div className="ll-container max-w-4xl">
          <ContentEyebrow>{c.eyebrow}</ContentEyebrow>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">
            {c.h1}
          </h1>
          <AccentLine />
        </div>
      </section>

      {/* Section 1 */}
      <section className="py-12 sm:py-16">
        <div className="ll-container max-w-3xl">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">{c.sec1.title}</h2>
          <p className="text-[#3E5871] font-[Inter] text-base sm:text-lg leading-relaxed">
            {c.sec1.body}
          </p>
        </div>
      </section>

      {/* Section 2 */}
      <section className="ll-section-soft py-12 sm:py-16">
        <div className="ll-container max-w-3xl">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">{c.sec2.title}</h2>
          <p className="text-[#3E5871] font-[Inter] text-base sm:text-lg leading-relaxed mb-4">
            {c.sec2.body1}
          </p>
          <p className="text-[#3E5871] font-[Inter] text-base sm:text-lg leading-relaxed">
            {c.sec2.body2}
          </p>
        </div>
      </section>

      {/* Section 3 */}
      <section className="py-12 sm:py-16">
        <div className="ll-container max-w-3xl">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">{c.sec3.title}</h2>
          <p className="text-[#3E5871] font-[Inter] text-base sm:text-lg leading-relaxed">
            {c.sec3.body}
          </p>
        </div>
      </section>

      {/* Bestuur */}
      <section className="ll-section-soft py-14 sm:py-20">
        <div className="ll-container">
          <div className="max-w-3xl mb-8">
            <ContentEyebrow>{c.bestuur.eyebrow}</ContentEyebrow>
            <h2 className="text-3xl sm:text-4xl font-bold mb-3">
              {c.bestuur.title}
            </h2>
            <AccentLine />
            <p className="text-[#3E5871] font-[Inter] text-base sm:text-lg leading-relaxed">
              {c.bestuur.body}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
            <figure className="rounded-2xl overflow-hidden shadow-md">
              <img
                src="/media/bestuur-biz-lemmer.jpg"
                alt="Bestuur BIZ Vereniging Lemmer"
                className="w-full h-auto object-cover"
                loading="lazy"
              />
              <figcaption className="text-xs text-[#5A7594] font-[Inter] px-4 py-3 bg-white">
                Het bestuur en de evenementencommissie van BIZ Vereniging Lemmer.
              </figcaption>
            </figure>

            <ul
              data-testid="bestuur-list"
              className="bg-white rounded-2xl border border-[#E4EEF5] divide-y divide-[#F0F5F9]"
            >
              {BESTUUR.map((m) => (
                <li
                  key={m.name}
                  className="flex items-start gap-3 p-4 sm:p-5"
                >
                  <div className="w-9 h-9 rounded-full bg-[#F4F8FB] text-[#2E9CD6] flex items-center justify-center shrink-0 mt-0.5">
                    <Users className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="font-[Poppins] font-semibold text-[#173A5E] text-base leading-tight">
                      {m.name}
                    </p>
                    <p className="text-sm text-[#5A7594] font-[Inter]">
                      {m.role}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-14 sm:py-20">
        <div className="ll-container max-w-3xl">
          <ContentEyebrow>Contact</ContentEyebrow>
          <h2 className="text-3xl sm:text-4xl font-bold mb-3">
            {c.contact.title}
          </h2>
          <AccentLine />
          <p className="text-[#3E5871] font-[Inter] text-base sm:text-lg leading-relaxed mb-6">
            {c.contact.body}
          </p>
          <p className="text-[#3E5871] font-[Inter] text-sm mb-6 inline-flex items-center gap-2">
            <Mail className="w-4 h-4 text-[#2E9CD6]" />
            <a
              href="mailto:info@lemmer-leeft.nl"
              className="text-[#2E9CD6] hover:text-[#173A5E] transition font-medium"
              data-testid="mailto-info"
            >
              info@lemmer-leeft.nl
            </a>
          </p>
          <ContactForm />
        </div>
      </section>
    </div>
  );
};

export default OverOnsPage;
